/**
 * Sync Meta Repository — KV store untuk metadata sinkronisasi.
 *
 * Berbeda dari repository lain, sync_meta menggunakan pola Key-Value
 * (bukan CRUD entity). Primary key adalah `key`, bukan `id`.
 *
 * Penyimpanan metadata seperti:
 * - last_sync_at: timestamp sync terakhir per entity type
 * - schema_version: versi skema cache
 * - active_workspace_id: workspace yang sedang aktif
 *
 * Acuan: docs/04-Database.md §6 (sync_meta), §9.3 (Delta Sync).
 */

import { requireDatabase } from '../sqlite-client';
import type { SyncMeta } from '../types';

export class SyncMetaRepository {
  private readonly tableName = 'sync_meta';

  private get db() {
    return requireDatabase();
  }

  private now(): string {
    return new Date().toISOString();
  }

  /** Ambil nilai berdasarkan key. Return null jika tidak ada. */
  async get(key: string): Promise<string | null> {
    const row = this.db.queryOne<{ value: string }>(
      `SELECT value FROM ${this.tableName} WHERE key = ?`,
      [key],
    );
    return row?.value ?? null;
  }

  /**
   * Set nilai (upsert — insert atau update).
   * Jika key sudah ada, value dan updated_at diperbarui.
   */
  async set(key: string, value: string): Promise<void> {
    this.db.run(
      `INSERT INTO ${this.tableName} (key, value, updated_at)
       VALUES (?, ?, ?)
       ON CONFLICT(key) DO UPDATE SET value = excluded.value, updated_at = excluded.updated_at`,
      [key, value, this.now()],
    );
  }

  /** Hapus key. */
  async delete(key: string): Promise<void> {
    this.db.run(`DELETE FROM ${this.tableName} WHERE key = ?`, [key]);
  }

  /** Ambil semua metadata. */
  async getAll(): Promise<SyncMeta[]> {
    return this.db.queryAll<SyncMeta>(`SELECT * FROM ${this.tableName}`);
  }

  // -------------------------------------------------------------------------
  // Convenience: typed getters
  // -------------------------------------------------------------------------

  /** Ambil nilai sebagai number. Return null jika tidak ada atau invalid. */
  async getNumber(key: string): Promise<number | null> {
    const value = await this.get(key);
    if (value === null) return null;
    const num = Number(value);
    return Number.isNaN(num) ? null : num;
  }

  /** Ambil nilai sebagai timestamp (ISO string). Return null jika tidak ada. */
  async getTimestamp(key: string): Promise<string | null> {
    return this.get(key);
  }

  /** Set nilai numerik. */
  async setNumber(key: string, value: number): Promise<void> {
    await this.set(key, String(value));
  }
}

// ---------------------------------------------------------------------------
// Predefined Keys — konstanta untuk key yang sering dipakai
// ---------------------------------------------------------------------------

export const SYNC_META_KEYS = {
  /** Timestamp sync terakhir untuk entity type tertentu. */
  lastSyncAt: (entityType: string) => `last_sync_at:${entityType}`,
  /** ID workspace yang sedang aktif di sesi ini. */
  activeWorkspaceId: 'active_workspace_id',
  /** Versi skema cache saat ini. */
  schemaVersion: 'schema_version',
  /** ID user yang memiliki cache ini. */
  cachedUserId: 'cached_user_id',
} as const;
